import React, { useState } from 'react';
import { 
  Mail, Phone, MapPin, Linkedin, Send, User, 
  MessageSquare, Clock, CheckCircle, AlertCircle,
  Smartphone, Mail as MailIcon, Shield, DollarSign
} from 'lucide-react';
import emailjs from '@emailjs/browser';

export default function Contact({ darkMode, language }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const isRTL = language === 'ar';
  
  const content = {
    fr: {
      title: "Travaillons Ensemble",
      subtitle: "Discutons de votre projet et trouvons la meilleure solution",
      
      name: "Votre Nom",
      email: "Votre Email",
      phone: "Téléphone (optionnel)",
      subject: "Sujet",
      projectType: "Type de projet",
      budget: "Budget estimé",
      message: "Décrivez votre projet",
      send: "Envoyer le message",
      
      projectTypes: [
        { value: 'website', label: 'Site Web' },
        { value: 'mobile', label: 'Application Mobile' },
        { value: 'webapp', label: 'Application Web' },
        { value: 'ecommerce', label: 'E-commerce' },
        { value: 'design', label: 'UI/UX Design' },
        { value: 'consulting', label: 'Consultation' },
        { value: 'other', label: 'Autre' }
      ],
      
      info: "Comment me contacter",
      availability: "Disponibilité",
      availabilityText: "Réponse garantie sous 24 heures",
      contactMethods: "Moyens de contact",
      
      successTitle: "Message envoyé !",
      successMessage: "Je vous répondrai dans les 24 heures.",
      errorTitle: "Erreur d'envoi",
      errorMessage: "Veuillez réessayer ou me contacter directement.",
      
      responseTime: "Réponse sous 24h",
      successRate: "100% de réponse",
      projectsCompleted: "10+ projets",
      support: "Support continu"
    },
    ar: {
      title: "لنعمل معًا",
      subtitle: "لنتحدث عن مشروعك ونجد أفضل حل",
      
      name: "اسمك",
      email: "بريدك الإلكتروني",
      phone: "الهاتف (اختياري)",
      subject: "الموضوع",
      projectType: "نوع المشروع",
      budget: "الميزانية المقدرة",
      message: "صف مشروعك",
      send: "إرسال الرسالة",
      
      projectTypes: [
        { value: 'website', label: 'موقع ويب' },
        { value: 'mobile', label: 'تطبيق موبايل' },
        { value: 'webapp', label: 'تطبيق ويب' },
        { value: 'ecommerce', label: 'متجر إلكتروني' },
        { value: 'design', label: 'تصميم UI/UX' },
        { value: 'consulting', label: 'استشارة' },
        { value: 'other', label: 'آخر' }
      ],
      
      info: "كيف تتصل بي",
      availability: "التوفر",
      availabilityText: "رد مضمون خلال 24 ساعة",
      contactMethods: "وسائل الاتصال",
      
      successTitle: "تم إرسال الرسالة!",
      successMessage: "سأرد عليك خلال 24 ساعة.",
      errorTitle: "خطأ في الإرسال",
      errorMessage: "يرجى إعادة المحاولة أو الاتصال بي مباشرة.",
      
      responseTime: "رد خلال 24 ساعة",
      successRate: "100% نسبة الرد",
      projectsCompleted: "10+ مشروع",
      support: "دعم مستمر"
    }
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const serviceID = 'service_fobvkbf';
      const templateID = 'template_oo7aqke';
      const publicKey = 'WAEz5JvCAaWEelHBy'; // Votre Public Key ajoutée
      
      await emailjs.send(
        serviceID,
        templateID,
        {
          from_name: formData.name,
          from_email: formData.email,
          from_phone: formData.phone || 'Non fourni',
          subject: formData.subject,
          project_type: formData.projectType || 'Non spécifié',
          budget: formData.budget || 'Non spécifié',
          message: formData.message,
          date: new Date().toLocaleString('fr-FR'),
          reply_to: formData.email
        },
        publicKey
      );

      setSubmitStatus('success');
      setSubmitMessage(t.successMessage);
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        projectType: '',
        budget: '',
        message: ''
      });

    } catch (error) {
      console.error('Erreur d\'envoi:', error);
      setSubmitStatus('error');
      setSubmitMessage(t.errorMessage);
      
      try {
        const mailtoLink = `mailto:raki39174@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
          `Nom: ${formData.name}\nEmail: ${formData.email}\nTéléphone: ${formData.phone || 'Non fourni'}\nType de projet: ${formData.projectType}\nBudget: ${formData.budget}\n\nMessage:\n${formData.message}`
        )}`;
        window.open(mailtoLink, '_blank');
      } catch (fallbackError) {
        console.error('Erreur fallback:', fallbackError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="px-4 pt-8 min-h-screen flex items-start">
      <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row gap-6 sm:gap-12 items-start">
        
        {/* Formulaire (gauche sur desktop) */}
        <div className="w-full sm:w-1/2">
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
            <h1 className={`text-2xl md:text-3xl font-bold mb-6 ${isRTL ? 'font-arabic' : ''} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t.title}
            </h1>
            <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-8 ${isRTL ? 'font-arabic' : ''}`}>
              {t.subtitle}
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-green-500">
                    <CheckCircle size={20} />
                    <span className="font-bold">{t.successTitle}</span>
                  </div>
                  <p className="text-green-500/80 mt-1">{submitMessage}</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-500">
                    <AlertCircle size={20} />
                    <span className="font-bold">{t.errorTitle}</span>
                  </div>
                  <p className="text-red-500/80 mt-1">{submitMessage}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.name} *
                  </label>
                  <div className="relative">
                    <User className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} size={18} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.email} *
                  </label>
                  <div className="relative">
                    <MailIcon className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} size={18} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.phone}
                  </label>
                  <div className="relative">
                    <Phone className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} size={18} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.subject} *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.projectType} *
                  </label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="">{isRTL ? 'اختر نوع المشروع' : 'Sélectionnez un type'}</option>
                    {t.projectTypes.map((type, index) => (
                      <option key={index} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                    {t.budget} *
                  </label>
                  <div className="relative">
                    <DollarSign className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} size={18} />
                    <input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors ${isRTL ? 'text-right' : ''}`}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={isRTL ? '' : ''}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block mb-2 text-sm font-medium ${isRTL ? 'text-right font-arabic' : ''}`}>
                  {t.message} *
                </label>
                <div className="relative">
                  <MessageSquare className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} size={18} />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className={`w-full ${isRTL ? 'pr-10' : 'pl-10'} px-4 py-3 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:border-blue-500 focus:outline-none transition-colors resize-none`}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    placeholder={isRTL ? 'صف مشروعك بالتفصيل...' : 'Décrivez votre projet en détail...'}
                  ></textarea>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg font-bold hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 ${isRTL ? 'font-arabic' : ''} ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>{isRTL ? 'جاري الإرسال...' : 'Envoi en cours...'}</span>
                  </>
                ) : (
                  <>
                    <span>{t.send}</span>
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Informations de contact (droite sur desktop) */}
        <div className="w-full sm:w-1/2 space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
              <Clock className="mx-auto mb-2 text-blue-500" size={24} />
              <div className="text-lg font-bold text-blue-500">{t.responseTime}</div>
            </div>
            <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
              <CheckCircle className="mx-auto mb-2 text-green-500" size={24} />
              <div className="text-lg font-bold text-green-500">{t.successRate}</div>
            </div>
            <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
              <Shield className="mx-auto mb-2 text-purple-500" size={24} />
              <div className="text-lg font-bold text-purple-500">{t.projectsCompleted}</div>
            </div>
            <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
              <MessageSquare className="mx-auto mb-2 text-cyan-500" size={24} />
              <div className="text-lg font-bold text-cyan-500">{t.support}</div>
            </div>
          </div>

          <div>
            <h3 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : ''}`}>
              <MessageSquare className="text-blue-500" size={24} />
              <span>{t.contactMethods}</span>
            </h3>
            
            <div className="space-y-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md hover:shadow-lg transition-shadow`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <Mail className="text-blue-500" size={28} />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h4 className={`font-bold text-lg mb-1 ${isRTL ? 'font-arabic' : ''}`}>Email Direct</h4>
                    <a 
                      href="mailto:raki39174@gmail.com" 
                      className="text-lg text-blue-500 hover:text-blue-600 transition-colors"
                    >
                      raki39174@gmail.com
                    </a>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                      {isRTL ? 'الطريقة الأسرع للاتصال' : 'Méthode de contact la plus rapide'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md hover:shadow-lg transition-shadow`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                    <Smartphone className="text-green-500" size={28} />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h4 className={`font-bold text-lg mb-1 ${isRTL ? 'font-arabic' : ''}`}>WhatsApp</h4>
                    <a 
                      href="https://wa.me/212771080256" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-lg text-green-500 hover:text-green-600 transition-colors"
                    >
                      +212 771 080 256
                    </a>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                      {isRTL ? 'محادثة فورية ومباشرة' : 'Chat instantané et direct'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md hover:shadow-lg transition-shadow`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <Linkedin className="text-blue-500" size={28} />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h4 className={`font-bold text-lg mb-1 ${isRTL ? 'font-arabic' : ''}`}>LinkedIn Professionnel</h4>
                    <a 
                      href="https://linkedin.com/in/erraki-abdelmajid-6903622a3" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-lg text-blue-500 hover:text-blue-600 transition-colors"
                    >
                      erraki-abdelmajid
                    </a>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                      {isRTL ? 'تواصل مهني وعرض مشاريع' : 'Connexion professionnelle et présentation de projets'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md hover:shadow-lg transition-shadow`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <MapPin className="text-purple-500" size={28} />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h4 className={`font-bold text-lg mb-1 ${isRTL ? 'font-arabic' : ''}`}>Localisation</h4>
                    <p className="text-lg text-gray-700 dark:text-gray-300">Casablanca, Maroc</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                      {isRTL ? 'مشاريع محلية ودولية' : 'Projets locaux et internationaux'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-gray-800 to-gray-900' : 'bg-gradient-to-r from-blue-50 to-cyan-50'} border ${darkMode ? 'border-gray-700' : 'border-blue-200'}`}>
            <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <Clock className="text-blue-500" size={24} />
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h4 className={`font-bold text-lg mb-2 ${isRTL ? 'font-arabic' : ''}`}>{t.availability}</h4>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 ${isRTL ? 'font-arabic' : ''}`}>
                  {t.availabilityText}
                </p>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                  <p className="mb-1">• {isRTL ? 'مشاريع فردية وفرق' : 'Projets individuels et d\'équipe'}</p>
                  <p className="mb-1">• {isRTL ? 'توظيف بدوام كامل وجزئي' : 'Emploi à temps plein et partiel'}</p>
                  <p>• {isRTL ? 'تعاون طويل وقصير المدى' : 'Collaborations longues et courtes durées'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}